import { PublicEventLocale, ScheduleItemV2 } from '@hopin-team/api-hopin';
import {
  Tab,
  TabHeader,
  TabList,
  TabPanel,
  TabPanels,
} from '@hopin-team/ui-tabs';
import { Text } from '@hopin-team/ui-text';
import { format, isBefore, startOfToday } from 'date-fns';
import React, { useContext, useEffect, useRef } from 'react';

import { getLocalisedDate } from '../..';
import { ScheduleTabsContext } from '../../providers';
import { PartSchedule } from '../../types';
import { Filters } from './Filters';
import { LazyTab } from './LazyTab';
import { TabsWithPadding } from './styles/scheduletabs-styles';

interface TabRenderProps {
  day: string;
  dayItems: ScheduleItemV2[];
  scheduleStart: Date;
  scheduleEnd: Date;
  timezone: string;
  panelTop: number;
}

interface ScheduleTabsProps {
  containerWidth?: number;
  dateLocale?: Locale;
  eventLocale?: PublicEventLocale;
  schedule?: PartSchedule;
  showEmptyTabs?: boolean;
  showFilters?: boolean;
  isVirtualEvent?: boolean;
  tabRender: (args: TabRenderProps) => any;
}

export const ScheduleTabs: React.FC<ScheduleTabsProps> = ({
  containerWidth,
  dateLocale,
  eventLocale,
  schedule,
  showEmptyTabs = true,
  showFilters = true,
  isVirtualEvent = true,
  tabRender,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    itemsByDay,
    scheduleStart,
    scheduleEnd,
    timezone,
    activeIndex,
    setTabIndex,
  } = useContext(ScheduleTabsContext);

  const tabHeaders = itemsByDay
    .map(([day, dayItems]) => {
      const date = new Date(day);
      const dateIgnoringTimezone = new Date(
        date.valueOf() + date.getTimezoneOffset() * 60 * 1000,
      );
      return {
        day,
        empty: !dayItems.length,
        label: format(dateIgnoringTimezone, 'LLLL dd', {
          locale: dateLocale,
        }),
        count: dayItems.length,
      };
    })
    .filter(header => showEmptyTabs || !header.empty);

  const matchingIndex = tabHeaders.findIndex(
    ({ day }) => !isBefore(getLocalisedDate(day), startOfToday()),
  );

  const defaultTabIndex = matchingIndex > -1 ? matchingIndex : 0;

  useEffect(
    () => {
      setTabIndex?.(defaultTabIndex);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <TabsWithPadding
      key={tabHeaders.length}
      pattern="main"
      index={activeIndex}
      defaultIndex={defaultTabIndex}
      onChange={setTabIndex}
    >
      <TabHeader>
        <TabList showScrollButtons>
          {tabHeaders.map(header => {
            return (
              <Tab key={header.day}>
                <Text px={4} scale={4} data-testid={header.day}>
                  {header.label}
                  <Text pattern="body" scale={3}>
                    {` (${header.count})`}
                  </Text>
                </Text>
              </Tab>
            );
          })}
        </TabList>
        {showFilters && (
          <Filters
            schedule={schedule}
            containerWidth={containerWidth}
            eventLocale={eventLocale}
            isVirtualEvent={isVirtualEvent}
          />
        )}
      </TabHeader>
      <TabPanels ref={ref}>
        {itemsByDay.map(([day, dayItems], index) => (
          <TabPanel key={day}>
            <LazyTab index={index}>
              {tabRender({
                dayItems,
                day,
                scheduleStart,
                scheduleEnd,
                timezone,
                panelTop: ref.current?.getBoundingClientRect().top || 0,
              })}
            </LazyTab>
          </TabPanel>
        ))}
      </TabPanels>
    </TabsWithPadding>
  );
};
